import Papa from 'papaparse';

// pdfmake + its bundled Roboto fonts weigh ~1.85 MB minified — far too
// much to load on every portal boot when the user might never click
// "Export PDF". We dynamic-import on demand the first time a PDF is
// requested. Subsequent calls re-use the cached promise / module.
//
// Cast through `any` because pdfmake's default-export typings don't
// match the actual UMD bundle shape.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let pdfMakePromise: Promise<any> | null = null;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function loadPdfMake(): Promise<any> {
  if (pdfMakePromise) return pdfMakePromise;
  pdfMakePromise = (async () => {
    const [pdfMakeMod, pdfFontsMod] = await Promise.all([
      import('pdfmake/build/pdfmake'),
      import('pdfmake/build/vfs_fonts'),
    ]);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pdfMake: any = (pdfMakeMod as any).default ?? pdfMakeMod;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fonts: any = (pdfFontsMod as any).default ?? pdfFontsMod;
    // pdfmake 0.3.x: vfs_fonts.js does `module.exports = vfs` — the
    // import default IS the vfs object. Older 0.2.x shape was a wrapper
    // `{ pdfMake: { vfs: ... } }`. Fall through both so a future
    // version bump doesn't silently break exports.
    const vfs = fonts.pdfMake?.vfs ?? fonts.vfs ?? fonts;
    if (typeof pdfMake.addVirtualFileSystem === 'function') {
      pdfMake.addVirtualFileSystem(vfs);
    } else {
      pdfMake.vfs = vfs;
    }
    return pdfMake;
  })();
  return pdfMakePromise;
}

export type ExportColumn<TRow> = {
  key: keyof TRow & string;
  label: string;
  format?: (value: unknown, row: TRow) => string;
};

/** Build a filename per PORTAL_FORM_RULES.md §8.3. */
export function buildExportFilename(entityName: string, scope = '', ext = 'csv'): string {
  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  const stamp =
    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-` +
    `${pad(now.getHours())}-${pad(now.getMinutes())}`;
  const safeScope = scope.replace(/[^A-Za-z0-9_-]/g, '_');
  const safeEntity = entityName.replace(/[^A-Za-z0-9_-]/g, '_');
  const parts = [safeEntity, safeScope, stamp].filter(Boolean);
  return `${parts.join('-')}.${ext}`;
}

export function exportRowsToCsv<TRow extends Record<string, unknown>>(
  rows: TRow[],
  columns: ExportColumn<TRow>[],
  filename: string,
): void {
  const data = rows.map((row) =>
    columns.reduce<Record<string, string>>((acc, col) => {
      const raw = row[col.key];
      acc[col.label] = col.format ? col.format(raw, row) : raw == null ? '' : String(raw);
      return acc;
    }, {}),
  );
  // BOM prefix so Excel recognises UTF-8.
  const csv = '﻿' + Papa.unparse(data, { header: true, newline: '\r\n' });
  triggerDownload(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), filename);
}

export async function exportRowsToPdf<TRow extends Record<string, unknown>>(
  rows: TRow[],
  columns: ExportColumn<TRow>[],
  filename: string,
  reportTitle: string,
  scope = '',
): Promise<void> {
  const pdfMake = await loadPdfMake();
  const headerRow = columns.map((c) => ({ text: c.label, style: 'th' }));
  const bodyRows = rows.map((row) =>
    columns.map((col) => {
      const raw = row[col.key];
      const text = col.format ? col.format(raw, row) : raw == null ? '' : String(raw);
      return { text, style: 'td' };
    }),
  );

  // pdfmake's typings are notoriously strict and don't model dynamic
  // header/footer functions cleanly; cast through `any` at the boundary.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const docDefinition: any = {
    pageOrientation: 'landscape' as const,
    pageMargins: [24, 56, 24, 36] as [number, number, number, number],
    info: { title: reportTitle, creator: 'ARMS' },
    header: {
      columns: [
        { text: 'ARMS', style: 'logo', width: 'auto', margin: [24, 16, 0, 0] },
        { text: reportTitle, style: 'reportTitle', alignment: 'center', margin: [0, 16, 0, 0] },
        {
          text: new Date().toLocaleString(),
          alignment: 'right',
          style: 'meta',
          margin: [0, 16, 24, 0],
        },
      ],
    },
    footer: (currentPage: number, pageCount: number) => ({
      columns: [
        { text: scope, style: 'meta', margin: [24, 0, 0, 0] },
        {
          text: `Page ${currentPage} of ${pageCount}`,
          alignment: 'right',
          style: 'meta',
          margin: [0, 0, 24, 0],
        },
      ],
    }),
    content: [
      {
        table: {
          headerRows: 1,
          body: [headerRow, ...bodyRows],
          widths: columns.map(() => '*'),
        },
        layout: 'lightHorizontalLines',
      },
    ],
    styles: {
      logo: { fontSize: 14, bold: true, color: '#1f3b6f' },
      reportTitle: { fontSize: 14, bold: true },
      meta: { fontSize: 8, color: '#888' },
      th: { bold: true, fontSize: 9, fillColor: '#f3f3f3' },
      td: { fontSize: 9 },
    },
    defaultStyle: { fontSize: 9 },
  };

  pdfMake.createPdf(docDefinition).download(filename);
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Cash-desk shift detail PDF (owner amendment 2026-08-23): a statement page
// per shift — headed with operator/site/shift identity, the merged
// single-line transaction list (latest first) and a balance summary.
// ---------------------------------------------------------------------------

export type ShiftDetailLine = {
  at: string;
  event: string;
  machine: string | null;
  amountCents: number | null;
  outcome: string | null;
  order: number;
  emphasis?: boolean;
};

type ShiftForPdf = {
  shiftNo: number | null; deskCode: string; terminalName: string | null;
  cashierName: string; status: string; gamingDay: string;
  openedAt: string; closedAt: string | null;
  openingCents: number; expectedCents: number | null; closingCents: number | null;
  varianceCents: number | null; transferCount: number; transferTotalCents: number;
  payoutCount: number; payoutTotalCents: number;
  originalClosingCents: number | null; amendedAt: string | null;
  amendedByName: string | null; amendNote: string | null;
};

export async function exportShiftDetailPdf(
  shift: ShiftForPdf, lines: ShiftDetailLine[], siteLabel: string,
): Promise<void> {
  const pdfMake = await loadPdfMake();
  const cents = (v: number | null) =>
    v === null ? '—' : (v / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  const dt = (v: string | null) => (v ? new Date(v).toLocaleString() : '—');
  const title = `Shift ${shift.shiftNo !== null ? `#${shift.shiftNo}` : shift.deskCode} — Transaction Detail`;

  const headerFields: Array<[string, string]> = [
    ['Site', siteLabel || '—'],
    ['Terminal', shift.terminalName ?? shift.deskCode],
    ['Cashier', shift.cashierName],
    ['Status', shift.status],
    ['Gaming day', new Date(shift.gamingDay).toLocaleDateString()],
    ['Opened', dt(shift.openedAt)],
    ['Closed', dt(shift.closedAt)],
    ['AFT-on transfers', `${shift.transferCount} (${cents(shift.transferTotalCents)})`],
    ['Cashouts paid', `${shift.payoutCount} (${cents(shift.payoutTotalCents)})`],
  ];

  const txHeader = ['When', 'Event', 'Machine', 'Amount', 'Result']
    .map((h) => ({ text: h, style: 'th' }));
  const txRows = lines.map((l) => [
    { text: l.at ? new Date(l.at).toLocaleString() : '—', style: 'td' },
    { text: l.event, style: l.emphasis ? 'tdBold' : 'td' },
    { text: l.machine ?? '—', style: 'td' },
    { text: l.amountCents !== null ? cents(l.amountCents) : '—', style: l.emphasis ? 'tdBold' : 'td', alignment: 'right' },
    { text: l.outcome ?? '—', style: 'td' },
  ]);

  const received = shift.transferTotalCents;
  const paidOut = shift.payoutTotalCents;
  const summary: Array<[string, string, boolean?]> = [
    ['Opening float', cents(shift.openingCents)],
    ['+ Money received (AFT-on)', cents(received)],
    ['− Money paid out (cashouts)', cents(-paidOut)],
    ['= Expected closing cash', cents(shift.expectedCents), true],
    ['Counted cash', cents(shift.closingCents)],
    ['Variance', cents(shift.varianceCents), true],
  ];
  if (shift.amendedAt && shift.originalClosingCents !== null) {
    summary.splice(5, 0, ['Original counted (before amendment)', cents(shift.originalClosingCents)]);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const docDefinition: any = {
    pageOrientation: 'portrait' as const,
    pageMargins: [32, 60, 32, 40] as [number, number, number, number],
    info: { title, creator: 'ARMS' },
    header: {
      columns: [
        { text: 'ARMS', style: 'logo', width: 'auto', margin: [32, 18, 0, 0] },
        { text: title, style: 'reportTitle', alignment: 'center', margin: [0, 18, 0, 0] },
        { text: new Date().toLocaleString(), alignment: 'right', style: 'meta', margin: [0, 18, 32, 0] },
      ],
    },
    footer: (currentPage: number, pageCount: number) => ({
      columns: [
        { text: siteLabel, style: 'meta', margin: [32, 0, 0, 0] },
        { text: `Page ${currentPage} of ${pageCount}`, alignment: 'right', style: 'meta', margin: [0, 0, 32, 0] },
      ],
    }),
    content: [
      {
        table: {
          widths: ['auto', '*', 'auto', '*'],
          body: chunkPairs(headerFields).map((pair) => pair.flatMap(([k, v]) => [
            { text: k, style: 'hk' }, { text: v, style: 'hv' },
          ])),
        },
        layout: 'noBorders',
        margin: [0, 0, 0, 12] as [number, number, number, number],
      },
      ...(shift.amendedAt ? [{
        text: `Counted cash amended ${dt(shift.amendedAt)}` +
          `${shift.amendedByName ? ` by ${shift.amendedByName}` : ''}` +
          `${shift.amendNote ? ` — ${shift.amendNote}` : ''}`,
        style: 'amend', margin: [0, 0, 0, 10] as [number, number, number, number],
      }] : []),
      { text: 'Transactions (latest first)', style: 'section' },
      {
        table: { headerRows: 1, widths: ['auto', '*', '*', 'auto', '*'], body: [txHeader, ...txRows] },
        layout: 'lightHorizontalLines',
        margin: [0, 4, 0, 14] as [number, number, number, number],
      },
      { text: 'Balance summary', style: 'section' },
      {
        table: {
          widths: ['*', 'auto'],
          body: summary.map(([k, v, strong]) => [
            { text: k, style: strong ? 'sumKeyBold' : 'sumKey' },
            { text: v, style: strong ? 'sumValBold' : 'sumVal', alignment: 'right' },
          ]),
        },
        layout: 'lightHorizontalLines',
        margin: [0, 4, 0, 0] as [number, number, number, number],
      },
    ],
    styles: {
      logo: { fontSize: 14, bold: true, color: '#1f3b6f' },
      reportTitle: { fontSize: 13, bold: true },
      meta: { fontSize: 8, color: '#888' },
      section: { fontSize: 11, bold: true, margin: [0, 6, 0, 2] },
      hk: { fontSize: 9, color: '#666', margin: [0, 2, 6, 2] },
      hv: { fontSize: 9, bold: true, margin: [0, 2, 16, 2] },
      amend: { fontSize: 9, italics: true, color: '#9a6b00' },
      th: { bold: true, fontSize: 9, fillColor: '#f3f3f3' },
      td: { fontSize: 9 },
      tdBold: { fontSize: 9, bold: true },
      sumKey: { fontSize: 10 },
      sumKeyBold: { fontSize: 10, bold: true },
      sumVal: { fontSize: 10 },
      sumValBold: { fontSize: 10, bold: true },
    },
    defaultStyle: { fontSize: 9 },
  };

  pdfMake.createPdf(docDefinition).download(
    buildExportFilename('Shift-detail', shift.shiftNo !== null ? `no${shift.shiftNo}` : shift.deskCode, 'pdf'));
}

function chunkPairs<T>(items: T[]): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += 2) {
    out.push(items.slice(i, i + 2).length === 2 ? items.slice(i, i + 2) : [items[i], items[i]]);
  }
  // Pad an odd trailing pair with an empty cell pair.
  if (items.length % 2 === 1) {
    const last = out[out.length - 1];
    last[1] = [ '', '' ] as unknown as T;
  }
  return out;
}
